import { useCallback } from 'react'
import classNames from 'classnames'
import { Transition } from '@headlessui/react'
import {
  InformationCircleIcon,
  MinusIcon,
  TicketIcon,
} from '@heroicons/react/24/outline'
import { InventoryItem, Venue } from 'types/model'

import { formatInventoryPrice } from '@modules/inventory/utils'
import { formatPrice } from '@utils/strings'
import { GuestCount } from './guest-count'
import { Button } from '@elements/button'
import { Skeleton } from '@elements/skeleton'
import { Popover } from '@elements/popover'
import { PlusIcon } from '@icons/plus'
import { usePopover } from '@hooks/popover'
import { InventorySubtotalDisplay } from './inventory-subtotal-display'

export interface InventoryCardProps {
  venue: Venue
  inventory: InventoryItem
  quantity?: number | null | undefined
  mode?: 'add' | 'multiply'
  onAdd?: (inv: InventoryItem) => any
  onMultiply?: (qty: number | null | undefined) => any
  maxQuantity?: number
}

function _InventoryCard({
  venue,
  inventory,
  quantity = 1,
  mode = 'add',
  onAdd,
  onMultiply,
}: InventoryCardProps) {
  const formattedPrice = formatInventoryPrice({ inventory, venue, quantity })
  const isZeroMinimumDeposit =
    inventory.paymentType === 'ADVANCE_DEPOSIT' &&
    inventory.price <= 0 &&
    (inventory.depositAmount ?? 0) > 0
  const displayPrice = isZeroMinimumDeposit
    ? formatPrice(inventory.depositAmount, {
        currency: venue.currencyCode,
        languageCode: venue.languageCode,
      })
    : inventory.price
      ? formattedPrice
      : 'FREE'

  const popover = usePopover()

  const isTicket = inventory.type === 'TICKET'
  const isPackage = inventory.type === 'PACKAGE'

  const handleAdd = useCallback(() => {
    onAdd?.(inventory)
  }, [inventory, onAdd])

  const handleDecrement = useCallback(() => {
    if (onMultiply && quantity && quantity !== 0) {
      onMultiply(quantity - 1)
    }
  }, [onMultiply, quantity])

  const handleIncrement = useCallback(() => {
    if (onMultiply && quantity !== inventory.quantityMax) {
      onMultiply((quantity ?? 0) + 1)
    }
  }, [inventory.quantityMax, onMultiply, quantity])

  const soldOut = inventory.quantityLeft <= 0
  return (
    <Transition
      as='div'
      show
      appear={mode === 'add'}
      className='w-full h-fit'
      enter='duration-150 ease-in'
      leave='duration-150 ease-out'
      enterFrom='translate-x-10 opacity-0'
      enterTo='translate-x-0 opacity-1'
      leaveFrom='translate-x-0 opacity-1'
      leaveTo='-translate-x-10 opacity-0'
    >
      <div
        className={classNames(
          'relative group w-full  h-fit p-4 border border-standard-xlight rounded-[3px] space-y-2 bg-card',
          {
            'shadow-md': mode === 'add',
            'text-neutral-400 dark:!text-neutral-500': soldOut,
          }
        )}
      >
        <div
          className={classNames(
            'max-w-full truncate text-ellipsis line-clamp-2 sm:line-clamp-1 sm:group-hover:line-clamp-3',
            'will-change-[max-height] transition-[max-height] duration-150 ease-in-out',
            'text-left text-[14px] leading-[16px] h-[34px] sm:text-body sm:h-auto sm:max-h-[24px] group-hover:sm:max-h-[72px]'
          )}
        >
          <span className='max-w-full font-bold whitespace-break-spaces !text-inherit'>
            {inventory.name}
          </span>
        </div>
        <div className='flex items-center justify-between space-x-4'>
          <div className='flex items-center justify-start flex-1'>
            {isPackage && <GuestCount count={inventory.guests} />}
            {isTicket && (
              <div className='flex items-center justify-start space-x-1.5'>
                <TicketIcon className='w-5 h-5' />
                <span className='text-base font-normal'>Admits 1 guest</span>
              </div>
            )}
          </div>
          <div className='flex items-center justify-end flex-1'>
            {soldOut && (
              <Button.Primary sm className='min-w-[96px]' disabled>
                Sold Out
              </Button.Primary>
            )}

            {!soldOut && mode === 'add' && (
              <div className='flex items-center justify-center space-x-3'>
                {venue.inventoryTableSubtotalPreviews &&
                  inventory.type === 'PACKAGE' && (
                    <div>
                      <Popover {...popover.props}>
                        <div className='-m-4 min-w-72 sm:min-w-96'>
                          <InventorySubtotalDisplay
                            inventory={inventory}
                            quantity={quantity}
                            venue={venue}
                          />
                        </div>
                      </Popover>
                      <div ref={popover.setAnchorElement}>
                        <InformationCircleIcon className='w-6 h-6 text-primary-200' />
                      </div>
                    </div>
                  )}

                <Button.Primary
                  onClick={handleAdd}
                  rightIcon={PlusIcon}
                  className='motion-safe:group-hover:shimmer min-w-[96px] drop-shadow-none hover:drop-shadow-none'
                  sm
                >
                  {displayPrice}
                </Button.Primary>
                {isTicket && !!inventory.price && (
                  <span className='absolute bottom-0.5 italic right-4 text-xs'>
                    All fees included
                  </span>
                )}
              </div>
            )}
            {!soldOut && mode === 'multiply' && (
              <div className='flex items-center justify-end space-x-4'>
                <span className='text-base font-bold'>
                  {displayPrice}
                </span>
                {isTicket && (
                  <>
                    <span className='text-sm font-bold'>X</span>
                    <div className='flex items-center justify-center space-x-0.5'>
                      <Button.Primary
                        sm
                        className='!px-[3px] text-center justify-center items-center !py-[3px] !rounded-full'
                        disabled={quantity === 0}
                        onClick={handleDecrement}
                      >
                        <MinusIcon className='w-3 h-3' />
                      </Button.Primary>
                      <span className='w-[24px] text-center'>{quantity}</span>
                      <div ref={popover.setAnchorElement}>
                        <Button.Primary
                          sm
                          className='!px-[3px] text-center justify-center items-center !py-[3px] !rounded-full'
                          disabled={quantity === inventory.quantityMax}
                          onClick={handleIncrement}
                        >
                          <PlusIcon className='w-3 h-3' />
                        </Button.Primary>
                      </div>
                      {!!inventory.quantityMax &&
                        inventory.quantityMax === quantity && (
                          <Popover {...popover.props}>
                            Max of {inventory.quantityMax} per customer
                          </Popover>
                        )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Transition>
  )
}

function InventoryCardSkeleton() {
  return (
    <div className='w-full h-fit p-3 border border-standard-xlight shadow-md rounded-[3px] space-y-2'>
      <div className='w-full h-6'>
        <Skeleton className='w-64 h-6 rounded shimmer' />
      </div>
      <div className='flex items-center justify-between'>
        <div className='flex items-center justify-start space-x-1'>
          <Skeleton className='w-5 h-5 rounded-full shimmer' />
          <Skeleton className='w-20 h-5 rounded shimmer' />
        </div>
        <Skeleton className='w-20 h-8 rounded-md shimmer' />
      </div>
    </div>
  )
}

export const InventoryCard = Object.assign(_InventoryCard, {
  Skeleton: InventoryCardSkeleton,
})
